import { useState } from "react";
import { ChartPie } from "lucide-react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from "recharts";
import { ChartExpandModal } from "./ChartExpandModal";
import { ChartTitleRow } from "./ChartTitleRow";
import { legendData, sliceData } from "./chartSlices";
import { legendView } from "./legendView";
import type { ChartSlice } from "./chartSlices";
import type { GroupBy, GroupCount } from "@/hooks/buildings/useBuildingDocumentStats";
import type { Translations } from "@/lib/i18n/types";

const GROUP_ORDER: GroupBy[] = ["category", "discipline", "stage"];

interface DocumentsByGroupChartProps {
  /** All three groupings at once — they all project from the same cached
   * available-filters response, so showing them together costs no extra
   * fetches. */
  data: Record<GroupBy, GroupCount[]>;
  isLoading: boolean;
  t: Translations;
}

interface DonutTooltipProps {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: ChartSlice }>;
}

function CustomTooltipContent({ active, payload }: DonutTooltipProps) {
  const entry = payload?.[0]?.payload;
  if (!active || !entry) return null;

  return (
    <div className="rounded-lg border bg-white p-2.5 shadow-md" style={{ fontSize: 12 }}>
      <div className="flex items-center justify-between gap-4 text-xs font-semibold">
        <span>{entry.name}</span>
        <span className="tabular-nums">{entry.count}</span>
      </div>
    </div>
  );
}

/** Entries flow inline and wrap to as many lines as the column needs, so the
 * legend fills the space beside the donut instead of stacking under it. The
 * count sits directly after its label, so the pair reads as one unit.
 *
 * `onShowAll` is passed on the dashboard only: past LEGEND_LIMIT the legend
 * ends with a "+N more" link that opens the expanded view. Inside the expanded
 * view every entry is listed, so there is nothing to expand and no link. */
function ChartLegend({
  data,
  onShowAll,
  t,
}: {
  data: ChartSlice[];
  onShowAll?: () => void;
  t: Translations;
}) {
  const d = t.buildingDetail.dashboard;
  const { visible, hiddenCount } = legendView(data, !!onShowAll);

  return (
    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-3 gap-y-1.5">
      {visible.map((slice, i) => (
        <div key={i} className="flex min-w-0 items-center gap-1.5 text-xs">
          <span
            className="h-2.5 w-2.5 flex-shrink-0 rounded-full"
            style={{ backgroundColor: slice.color }}
          />
          <span className="truncate text-muted-foreground">{slice.name}</span>
          <span className="font-medium tabular-nums">{slice.count}</span>
        </div>
      ))}
      {hiddenCount > 0 && (
        <button
          type="button"
          onClick={onShowAll}
          className="text-xs text-muted-foreground underline-offset-2 hover:text-neutral-700 hover:underline"
        >
          {d.legendShowMore.replace("{count}", String(hiddenCount))}
        </button>
      )}
    </div>
  );
}

/** One grouping: a donut whose centre hole carries the grouping's name (no
 * separate title above it), paired with its legend to the right. `slices` is
 * the bucketed donut data (top entries + "Other"); `legend` lists every entry
 * with its donut colour (gray for the ones inside "Other"). */
function GroupDonut({
  label,
  slices,
  legend,
  size,
  onShowAll,
  t,
}: {
  label: string;
  slices: ChartSlice[];
  legend: ChartSlice[];
  size: number;
  onShowAll?: () => void;
  t: Translations;
}) {
  const d = t.buildingDetail.dashboard;

  return (
    // items-start, not items-center: the legend's height varies per grouping,
    // and a centred row would drop each donut to a different vertical offset.
    // The donut box is fixed at `size` in both axes, so however the legend
    // wraps it can never move or resize the donut.
    <div className="flex items-start gap-4">
      <div className="relative flex-shrink-0" style={{ width: size, height: size }}>
        {slices.length === 0 ? (
          // Still name the grouping — otherwise an empty donut gives no clue
          // which of the three has no data.
          <div className="flex aspect-square w-full flex-col items-center justify-center gap-1">
            <ChartPie className="mb-1 h-6 w-6 text-muted-foreground/40" />
            <span className="text-xs font-medium text-neutral-700">{label}</span>
            <span className="text-xs text-muted-foreground">{d.noData}</span>
          </div>
        ) : (
          <div className="aspect-square w-full" style={{ overflow: "visible" }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart style={{ overflow: "visible" }}>
                <Pie
                  data={slices}
                  dataKey="count"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  innerRadius="70%"
                  outerRadius="100%"
                  strokeWidth={0}
                >
                  {slices.map((slice, i) => (
                    <Cell key={i} fill={slice.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={<CustomTooltipContent />}
                  isAnimationActive={false}
                  wrapperStyle={{ zIndex: 50 }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
        {slices.length > 0 && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="px-2 text-center text-xs font-medium text-neutral-700">{label}</span>
          </div>
        )}
      </div>
      <ChartLegend data={legend} onShowAll={onShowAll} t={t} />
    </div>
  );
}

export function DocumentsByGroupChart({ data, isLoading, t }: DocumentsByGroupChartProps) {
  const [expanded, setExpanded] = useState(false);
  const d = t.buildingDetail.dashboard;

  const groupLabels: Record<GroupBy, string> = {
    category: d.groupCategory,
    discipline: d.groupDiscipline,
    stage: d.groupStage,
  };

  const donut = (group: GroupBy, size: number, onShowAll?: () => void) => (
    <GroupDonut
      label={groupLabels[group]}
      slices={sliceData(data[group], d.other)}
      legend={legendData(data[group])}
      size={size}
      onShowAll={onShowAll}
      t={t}
    />
  );

  return (
    <>
      <div className="overflow-visible">
        <ChartTitleRow
          title={d.documentsByGroup}
          onExpand={() => setExpanded(true)}
          expandLabel={d.expandChart}
        />
        {isLoading ? (
          <div className="flex h-[180px] items-center justify-center text-sm text-muted-foreground">
            Loading...
          </div>
        ) : (
          // Three equal columns, split by thin vertical hairlines; they stack
          // (with horizontal hairlines) once the row gets too narrow for three
          // donuts. Every column carries the SAME px-4, so each donut sits at
          // the identical offset inside its column — the -mx-4 on the row
          // cancels the enclosing DashboardPanel's p-4, keeping the first
          // donut flush with the section's title row.
          <div className="-mx-4 mt-3 grid grid-cols-1 divide-y divide-neutral-200 md:grid-cols-3 md:divide-x md:divide-y-0">
            {GROUP_ORDER.map((group) => (
              <div key={group} className="min-w-0 px-4 py-5 first:pt-0 last:pb-0 md:py-0">
                {donut(group, 132, () => setExpanded(true))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Expanded view: the three groupings stack so each donut renders at full
          size next to its complete legend — nothing truncates, nothing clips. */}
      <ChartExpandModal
        open={expanded}
        onOpenChange={setExpanded}
        title={d.documentsByGroup}
        contentClassName="max-w-4xl"
      >
        <div className="flex flex-col divide-y divide-neutral-200">
          {GROUP_ORDER.map((group) => (
            <div key={group} className="py-6 first:pt-0 last:pb-0">
              {donut(group, 172)}
            </div>
          ))}
        </div>
      </ChartExpandModal>
    </>
  );
}
